import React, { useEffect, useState, useCallback, useRef } from 'react';
import AdminRaceLogDownload from '../components/AdminRaceLogDownload';
import { Link } from 'react-router-dom';
import AdminKeyGate from '../components/AdminKeyGate';
import RaceBadge from '../components/RaceBadge';
import AdminRaceDetails from '../components/AdminRaceDetails';
import PubSyncControls from '../components/PubSyncControls';
import {
  getAdminRaces, syncSeason, lockRace, unlockRace,
  postResults, getEntries, deleteEntry, autofillResults, setAdminKey, exportEntries, setRaceCode, setRaceVideo, setRaceVideoPublic, uploadRaceVideo, setManualLive, getOpenF1Status, cancelRace,
  getStaffEmails, addStaffEmail, removeStaffEmail,
} from '../api';

const kf = `
  @keyframes statusPulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
  }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// ── Race state classifier ─────────────────────────────────────────────────────
function getRaceState(race) {
  if (race.cancelled) return 'cancelled';
  if (race.official_top10) return 'complete';
  if (race.locked) return 'locked';
  if (race.race_date && new Date(race.race_date) < Date.now()) return 'overdue';
  if (race.code_required || race.entry_code) return 'open';
  return 'upcoming';
}

const STATE_CFG = {
  complete:  { border: '#1a331a', bg: 'transparent',              dot: '#22c55e', label: 'COMPLETE',           pulse: false },
  locked:    { border: '#e8002d', bg: 'rgba(232,0,45,0.035)',     dot: '#e8002d', label: 'AWAITING RESULTS',   pulse: true  },
  overdue:   { border: '#f59e0b', bg: 'rgba(245,158,11,0.035)',   dot: '#f59e0b', label: 'ACTION REQUIRED',    pulse: true  },
  open:      { border: '#22c55e', bg: 'transparent',              dot: null,      label: null,                 pulse: false },
  upcoming:  { border: '#1e1e1e', bg: 'transparent',              dot: null,      label: null,                 pulse: false },
  cancelled: { border: '#3a1a1a', bg: 'transparent',              dot: '#ef4444', label: 'CANCELLED',          pulse: false },
};

// ── Shared button styles ──────────────────────────────────────────────────────
const btn = {
  base: { padding: '7px 14px', borderRadius: 4, border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-body)' },
  yellow:  { background: '#f9b000', color: '#111' },
  amber:   { background: '#f59e0b', color: '#111' },
  ghost:   { background: '#1e1e1e', color: '#666', border: '1px solid #2a2a2a' },
  danger:  { background: 'transparent', color: '#ef4444', border: '1px solid #3a1a1a' },
  muted:   { background: '#1a1a1a', color: '#555' },
};

// ── Manual live top 10 ────────────────────────────────────────────────────────
function ManualLiveForm({ race, onRefresh }) {
  const init = () => Array.from({ length: 10 }, (_, i) => race.manual_top10?.[i] || '');
  const [top10, setTop10] = useState(init);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const inputs = React.useRef([]);

  // Sync if race prop changes (e.g. after push)
  useEffect(() => { setTop10(init()); }, [race.manual_top10]); // eslint-disable-line

  const handleChange = (i, val) => {
    const next = [...top10];
    next[i] = val.toUpperCase().slice(0, 3);
    setTop10(next);
    if (val.length >= 3 && i < 9) inputs.current[i + 1]?.focus();
  };

  const handlePush = async () => {
    const filled = top10.map(v => v.trim());
    if (filled.some(v => !v)) return;
    setSaving(true);
    try {
      await setManualLive(race.id, filled);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onRefresh();
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  const handleClear = async () => {
    setSaving(true);
    try { await setManualLive(race.id, null); setTop10(Array(10).fill('')); onRefresh(); }
    catch { /* silent */ }
    finally { setSaving(false); }
  };

  const isDirty = top10.some((v, i) => v !== (race.manual_top10?.[i] || ''));
  const isComplete = top10.every(v => v.trim().length === 3);

  return (
    <div style={{ marginTop: 10, padding: '12px 14px', background: '#0a1a0a', border: '1px solid #1a3a1a', borderRadius: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#22c55e', letterSpacing: '0.12em', textTransform: 'uppercase', flex: 1 }}>
          ▶ Manual Live Order {race.manual_top10 ? '· Active' : '· Not set'}
        </span>
        {race.manual_top10 && (
          <button style={{ ...btn.base, ...btn.danger, padding: '3px 10px', fontSize: 11 }} onClick={handleClear} disabled={saving}>
            Clear
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
        {top10.map((v, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: '#555' }}>P{i + 1}</span>
            <input
              ref={el => inputs.current[i] = el}
              style={{
                width: 46, padding: '6px 2px', textAlign: 'center',
                background: '#0d0d0d', color: v ? '#f0f0f0' : '#444', fontSize: 13,
                fontFamily: 'var(--font-mono)', textTransform: 'uppercase',
                border: `1px solid ${v.length === 3 ? '#22c55e44' : '#1e1e1e'}`,
                borderRadius: 4, outline: 'none',
              }}
              maxLength={3}
              value={v}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => e.key === 'Backspace' && !v && i > 0 && inputs.current[i - 1]?.focus()}
              placeholder="ACR"
            />
          </div>
        ))}
      </div>
      <button
        style={{
          ...btn.base,
          background: saved ? '#14532d' : isComplete && isDirty ? '#22c55e' : '#1a2a1a',
          color: saved ? '#86efac' : isComplete && isDirty ? '#111' : '#555',
          width: '100%', fontFamily: 'var(--font-display)', fontSize: 14,
          textTransform: 'uppercase', letterSpacing: '0.06em',
          transition: 'background 0.15s, color 0.15s',
        }}
        onClick={handlePush}
        disabled={saving || !isComplete}
      >
        {saving ? 'Pushing…' : saved ? '✓ Pushed' : 'Push Live →'}
      </button>
    </div>
  );
}

// ── Results form ──────────────────────────────────────────────────────────────
function ResultsForm({ race, onDone, onStatus }) {
  const [top10, setTop10]         = useState(Array.from({ length: 10 }, () => ''));
  const [autofilled, setAutofilled] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [err, setErr]             = useState(null);

  const handleAutofill = async () => {
    setAutofilling(true); setErr(null);
    try {
      const { top10: fetched, warning } = await autofillResults(race.id);
      setTop10(fetched);
      setAutofilled(true);
      setConfirming(false);
      if (warning) setErr(`⚠ ${warning}`);
    } catch (ex) { setErr(ex.response?.data?.error || ex.message); }
    finally { setAutofilling(false); }
  };

  const handleChange = (i, val) => {
    const next = [...top10]; next[i] = val; setTop10(next);
    setAutofilled(false); setConfirming(false);
  };

  const handleSubmit = async () => {
    const filled = top10.map(v => v.trim().toUpperCase()).filter(Boolean);
    if (filled.length !== 10) return setErr('All 10 positions required');
    setLoading(true); setErr(null);
    try {
      await postResults(race.id, filled);
      onStatus('success', `Results posted for ${race.name}`);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.error || ex.message); }
    finally { setLoading(false); setConfirming(false); }
  };

  return (
    <div style={{ marginTop: 16, padding: '16px', background: '#0d0d0d', borderRadius: 6, animation: 'slideDown 0.2s ease' }}>
      {/* Auto-fill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button
          type="button"
          style={{ ...btn.base, ...btn.ghost }}
          onClick={handleAutofill}
          disabled={autofilling}
        >
          {autofilling ? 'Fetching…' : '↓ Auto-fill from OpenF1'}
        </button>
        {autofilled && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#22c55e' }}>
            ✓ Fetched from OpenF1 — verify before posting
          </span>
        )}
      </div>

      {/* 10 inputs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {top10.map((v, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#555' }}>P{i + 1}</span>
            <input
              style={{
                width: 54, padding: '7px 4px', textAlign: 'center',
                background: '#111', color: '#f0f0f0', fontSize: 13,
                fontFamily: 'var(--font-mono)', textTransform: 'uppercase',
                border: `1px solid ${autofilled && v ? '#22c55e' : '#2a2a2a'}`,
                borderRadius: 4,
              }}
              maxLength={3}
              value={v}
              onChange={e => handleChange(i, e.target.value)}
              placeholder="ACR"
            />
          </div>
        ))}
      </div>

      {err && (
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#f9b000', marginBottom: 12 }}>{err}</p>
      )}

      {/* Post button — two-step confirm */}
      {!confirming ? (
        <button
          style={{ ...btn.base, ...btn.yellow, width: '100%', fontFamily: 'var(--font-display)', fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '11px 0' }}
          onClick={() => setConfirming(true)}
          disabled={loading}
        >
          Post Results →
        </button>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={{ ...btn.base, ...btn.yellow, flex: 1, fontFamily: 'var(--font-display)', fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.06em' }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Posting…' : '✓ Confirm & Post'}
          </button>
          <button style={{ ...btn.base, ...btn.ghost }} onClick={() => setConfirming(false)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Poster generator ─────────────────────────────────────────────────────────
function generatePoster(race) {
  const url     = `https://f1tips.se/races/${race.id}`;
  const code    = race.entry_code || null;
  const tagline = `Round ${race.round} · ${race.name}`;

  const codeSection = code ? `
  <div class="code-section">
    <div class="code-eyebrow">Race Code</div>
    <div class="code-value">${code}</div>
    <div class="code-hint">Enter this code on the site to unlock your prediction for this race</div>
  </div>` : '';

  // When a code is set the real app flow is: code gate → name/email → pick top 10
  const step2 = code
    ? `Enter the <strong>race code</strong> when prompted — see below`
    : `Enter your <strong>name and email address</strong>`;
  const step3 = code
    ? `Enter your <strong>name and email address</strong>`
    : `Pick your <strong>top 10 finishers</strong> before the race starts`;
  const step4body = code
    ? `Pick your <strong>top 10 finishers</strong> before the race starts — predictions lock at lights out`
    : `Exact position match = <em>F1 championship points</em> &nbsp;·&nbsp; <strong>Best total across the season wins!</strong>`;
  const step5 = code ? `
    <div class="step">
      <div class="step-num-col"><span class="step-num">5</span></div>
      <div class="step-body">
        <div class="step-text">Exact position match = <em>F1 championship points</em> &nbsp;·&nbsp; <strong>Best total across the season wins!</strong></div>
      </div>
    </div>` : '';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>F1 Pool · ${race.name}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:ital,wght@0,400;0,600;0,700;0,800;0,900;1,900&family=DM+Sans:wght@300;400;500&family=JetBrains+Mono:wght@400;500;700;800&display=swap" rel="stylesheet">
  <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>
  <style>
    @page { size: A4 portrait; margin: 0; }
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { background: #c8c8c8; }
    body {
      width: 210mm; height: 297mm; margin: 0 auto;
      background: #f8f6f2; color: #1a1a1a;
      font-family: 'DM Sans', sans-serif;
      position: relative; overflow: hidden;
    }
    @media print { html { background: none; } body { margin: 0; } }
    body::before {
      content: ''; position: absolute; inset: 0;
      background-image: radial-gradient(circle, rgba(0,0,0,0.07) 1px, transparent 1px);
      background-size: 18px 18px; pointer-events: none; z-index: 0;
    }
    .stripe { position: absolute; left: 0; top: 0; bottom: 0; width: 6px; background: #e8002d; z-index: 10; }
    .page { position: relative; z-index: 1; padding: 11mm 14mm 11mm 20mm; height: 100%; display: flex; flex-direction: column; }
    .brand-line { font-family: 'Barlow Condensed', sans-serif; font-size: 8.5pt; font-weight: 700; color: #b0a898; text-transform: uppercase; letter-spacing: 0.26em; margin-bottom: 1.5mm; }
    .hero-wrap { position: relative; padding-right: 62mm; margin-bottom: 4mm; }
    .hero { line-height: 0.86; }
    .hero-f1 { display: block; font-family: 'Barlow Condensed', sans-serif; font-size: 112pt; font-weight: 900; text-transform: uppercase; color: #f9b000; letter-spacing: -0.02em; }
    .hero-pool { display: block; font-family: 'Barlow Condensed', sans-serif; font-size: 112pt; font-weight: 900; text-transform: uppercase; color: #1a1a1a; letter-spacing: -0.02em; }
    .hero-qr { position: absolute; top: 4mm; right: 0; background: #fff; border-radius: 6px; padding: 3mm; width: 56mm; height: 56mm; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 12px rgba(0,0,0,0.10); }
    .hero-qr canvas, .hero-qr img { width: 100% !important; height: 100% !important; display: block; }
    .tagline { font-family: 'Barlow Condensed', sans-serif; font-size: 15pt; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 0.14em; margin-bottom: 7mm; }
    .rule { height: 3px; background: linear-gradient(90deg, #f9b000 0%, #e8002d 40%, transparent 100%); margin-bottom: 7mm; border: none; }
    .section-label { font-family: 'JetBrains Mono', monospace; font-size: 7pt; font-weight: 700; color: #e8002d; letter-spacing: 0.24em; text-transform: uppercase; margin-bottom: 4mm; }
    .steps { display: flex; flex-direction: column; gap: 2mm; margin-bottom: 8mm; }
    .step { display: flex; align-items: stretch; gap: 0; border-radius: 3px; overflow: hidden; background: rgba(0,0,0,0.04); border: 1px solid rgba(0,0,0,0.09); }
    .step-num-col { display: flex; align-items: center; justify-content: center; padding: 3mm 4mm; background: rgba(0,0,0,0.05); border-right: 1px solid rgba(0,0,0,0.09); flex-shrink: 0; width: 14mm; }
    .step-num { font-family: 'Barlow Condensed', sans-serif; font-size: 26pt; font-weight: 900; color: rgba(212,148,10,0.75); line-height: 1; }
    .step-body { padding: 3.5mm 5mm; display: flex; align-items: center; }
    .step-text { font-family: 'DM Sans', sans-serif; font-size: 11pt; font-weight: 300; color: #555; line-height: 1.35; }
    .step-text strong { color: #111; font-weight: 600; }
    .step-text em { color: #d4940a; font-style: normal; font-weight: 600; }
    .code-section { background: rgba(249,176,0,0.10); border: 2px solid rgba(212,148,10,0.45); border-left: 6px solid #f9b000; border-radius: 5px; padding: 6mm 8mm; margin-bottom: 8mm; display: flex; flex-direction: column; align-items: center; text-align: center; box-shadow: 0 2px 16px rgba(212,148,10,0.12); }
    .code-eyebrow { font-family: 'JetBrains Mono', monospace; font-size: 7.5pt; font-weight: 700; color: #999; letter-spacing: 0.22em; text-transform: uppercase; margin-bottom: 2.5mm; }
    .code-value { font-family: 'JetBrains Mono', monospace; font-size: 42pt; font-weight: 800; color: #d4940a; letter-spacing: 0.12em; line-height: 1; margin-bottom: 2.5mm; }
    .code-hint { font-family: 'DM Sans', sans-serif; font-size: 8.5pt; font-weight: 400; color: #777; letter-spacing: 0.02em; }
    .bottom { margin-top: auto; padding-top: 5mm; border-top: 2px solid #c8c2ba; }
    .url-line { font-family: 'Barlow Condensed', sans-serif; font-size: 38pt; font-weight: 900; text-transform: uppercase; letter-spacing: 0.02em; line-height: 0.9; margin-bottom: 2.5mm; }
    .url-line .url-domain { color: #d4940a; }
    .url-line .url-tld { color: #1a1a1a; }
    .fine { font-family: 'JetBrains Mono', monospace; font-size: 7pt; color: #aaa; letter-spacing: 0.1em; text-transform: uppercase; }
    .fine span { color: #ccc; margin: 0 2mm; }
  </style>
</head>
<body>
<div class="stripe"></div>
<div class="page">
  <div class="brand-line">Bryggeriet Ångkvarn presents</div>
  <div class="hero-wrap">
    <div class="hero">
      <span class="hero-f1">F1</span>
      <span class="hero-pool">Pool</span>
    </div>
    <div class="hero-qr" id="qr-container"></div>
  </div>
  <div class="tagline">${tagline}</div>
  <hr class="rule">
  <div class="section-label">How to join</div>
  <div class="steps">
    <div class="step">
      <div class="step-num-col"><span class="step-num">1</span></div>
      <div class="step-body"><div class="step-text">Scan the QR code or go to <strong>f1tips.se/races/${race.id}</strong></div></div>
    </div>
    <div class="step">
      <div class="step-num-col"><span class="step-num">2</span></div>
      <div class="step-body"><div class="step-text">${step2}</div></div>
    </div>
    <div class="step">
      <div class="step-num-col"><span class="step-num">3</span></div>
      <div class="step-body"><div class="step-text">${step3}</div></div>
    </div>
    <div class="step">
      <div class="step-num-col"><span class="step-num">4</span></div>
      <div class="step-body"><div class="step-text">${step4body}</div></div>
    </div>
    ${step5}
  </div>
  ${codeSection}
  <div class="bottom">
    <div class="url-line"><span class="url-domain">f1tips</span><span class="url-tld">.se</span></div>
    <div class="fine">Free to play <span>·</span> 2026 F1 Season <span>·</span> Bryggeriet Ångkvarn</div>
  </div>
</div>
<script>
new QRCode(document.getElementById('qr-container'), {
  text: '${url}',
  width: 256,
  height: 256,
  colorDark: '#000000',
  colorLight: '#ffffff',
  correctLevel: QRCode.CorrectLevel.M
});
</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
}

// ── Single race card ──────────────────────────────────────────────────────────
function RaceAdmin({ race, onRefresh, isNext }) {
  const [entries, setEntries]         = useState([]);
  const [showEntries, setShowEntries]   = useState(false);
  const [showResults, setShowResults]   = useState(false);
  const [showEditResults, setShowEditResults] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [status, setStatus]           = useState(null); // { type, msg, time }
  const [codeInput, setCodeInput]   = useState(race.entry_code || '');
  const [codeSaving, setCodeSaving] = useState(false);
  const [codeStatus, setCodeStatus] = useState(null); // 'saved' | 'error'
  const [videoInput, setVideoInput]   = useState(race.video_url || '');
  const [videoSaving, setVideoSaving] = useState(false);
  const [videoStatus, setVideoStatus] = useState(null); // 'saved' | 'error'
  const [videoTab, setVideoTab]       = useState('url'); // 'url' | 'upload'
  const [videoEditing, setVideoEditing] = useState(false);
  const [uploadFile, setUploadFile]   = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null); // 0-100 or null
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    setCodeInput(race.entry_code || '');
  }, [race.entry_code]);

  useEffect(() => {
    setVideoInput(race.video_url || '');
    setVideoEditing(false);
  }, [race.video_url]);

  const state  = getRaceState(race);
  const cfg    = STATE_CFG[state === 'upcoming' && isNext ? 'overdue' : state];
  // treat "next upcoming" with amber styling (reuse overdue cfg colour but different label)
  const effectiveCfg = (state === 'upcoming' && isNext)
    ? { ...STATE_CFG.overdue, label: 'NEXT RACE', dot: '#f9b000', pulse: false }
    : cfg;

  const setStatusMsg = (type, msg) => setStatus({
    type, msg,
    time: new Date().toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }),
  });

  const doLock = async () => {
    try { await lockRace(race.id); onRefresh(); setStatusMsg('success', 'Race locked'); }
    catch (e) { setStatusMsg('error', e.response?.data?.error || e.message); }
  };

  const doUnlock = async () => {
    try { await unlockRace(race.id); onRefresh(); setStatusMsg('success', 'Race unlocked'); }
    catch (e) { setStatusMsg('error', e.response?.data?.error || e.message); }
  };

  const doCancel = async () => {
    try { await cancelRace(race.id, !race.cancelled); onRefresh(); setStatusMsg('success', race.cancelled ? 'Race reinstated' : 'Race marked as cancelled'); }
    catch (e) { setStatusMsg('error', e.response?.data?.error || e.message); }
  };

  const loadEntries = async () => {
    try {
      const data = await getEntries(race.id);
      setEntries(data); setShowEntries(true);
    } catch (e) { setStatusMsg('error', 'Failed to load entries'); }
  };

  const doDelete = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await deleteEntry(race.id, entryId);
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setStatusMsg('success', 'Entry deleted');
    } catch (e) { setStatusMsg('error', e.response?.data?.error || e.message); }
  };

  const saveCode = async (code) => {
    setCodeSaving(true); setCodeStatus(null);
    try {
      await setRaceCode(race.id, code || null);
      setCodeStatus('saved');
      onRefresh();
    } catch {
      setCodeStatus('error');
    } finally {
      setCodeSaving(false);
      setTimeout(() => setCodeStatus(null), 2000);
    }
  };

  const saveVideo = async (url) => {
    setVideoSaving(true); setVideoStatus(null);
    try {
      await setRaceVideo(race.id, url || null);
      setVideoStatus('saved');
      onRefresh();
    } catch {
      setVideoStatus('error');
    } finally {
      setVideoSaving(false);
      setTimeout(() => setVideoStatus(null), 2000);
    }
  };

  const toggleVideoPublic = async () => {
    try {
      await setRaceVideoPublic(race.id, !race.video_public);
      onRefresh();
    } catch (e) { setStatusMsg('error', e.response?.data?.error || e.message); }
  };

  const doUpload = async () => {
    if (!uploadFile) return;
    setUploadProgress(0); setUploadError(null);
    try {
      await uploadRaceVideo(race.id, uploadFile, pct => {
        setUploadProgress(pct < 100 ? pct : 100);
      });
      setVideoStatus('saved');
      setUploadFile(null); setUploadProgress(null);
      onRefresh();
      setTimeout(() => setVideoStatus(null), 2000);
    } catch (e) {
      setUploadError(e.response?.data?.error || e.message || 'Upload failed');
      setUploadProgress(null);
    }
  };

  const isComplete = state === 'complete';
  const isCancelled = state === 'cancelled';

  return (
    <div style={{
      background: effectiveCfg.bg,
      borderRadius: 6,
      borderLeft: `4px solid ${effectiveCfg.border}`,
      border: `1px solid #1a1a1a`,
      borderLeftWidth: 4,
      borderLeftColor: effectiveCfg.border,
      padding: 16,
      marginBottom: 10,
      opacity: isComplete && !showDetails && !showEditResults ? 0.65 : 1,
      transition: 'opacity 0.2s',
    }}>

      {/* ── Card header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isComplete ? 0 : 12 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13, color: '#f9b000', flexShrink: 0 }}>
          R{race.round}
        </span>
        <span style={{ fontWeight: 600, flex: 1, color: isComplete && !showDetails ? '#555' : '#e0e0e0', fontSize: 14 }}>
          {race.name}
        </span>
        <RaceBadge race={race} />
        {effectiveCfg.label && (
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
            color: effectiveCfg.dot, letterSpacing: '0.1em',
            display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
          }}>
            {effectiveCfg.pulse && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: effectiveCfg.dot, display: 'inline-block', animation: 'statusPulse 1.4s ease-in-out infinite' }} />
            )}
            {effectiveCfg.label}
          </span>
        )}
      </div>

      {/* ── Actions (hidden when complete) ── */}
      {!isComplete && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* Cancel / Uncancel */}
            <button
              style={{ ...btn.base, ...(isCancelled ? btn.ghost : btn.danger), fontSize: 12 }}
              onClick={doCancel}
            >
              {isCancelled ? 'Reinstate Race' : 'Mark Cancelled'}
            </button>

            {/* Lock / Unlock — hidden when cancelled */}
            {!isCancelled && (!race.locked
              ? <button style={{ ...btn.base, ...btn.amber }} onClick={doLock}>Lock Race</button>
              : <button style={{ ...btn.base, ...btn.ghost, fontSize: 12 }} onClick={doUnlock}>Unlock</button>
            )}

            {/* Post Results — primary CTA when locked */}
            {!isCancelled && race.locked && !race.official_top10 && (
              <button
                style={{ ...btn.base, ...btn.yellow, fontFamily: 'var(--font-display)', fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}
                onClick={() => setShowResults(r => !r)}
              >
                {showResults ? 'Cancel' : 'Post Results →'}
              </button>
            )}

            {/* Print Poster */}
            <button
              style={{ ...btn.base, ...btn.ghost, fontSize: 12 }}
              onClick={() => generatePoster(race)}
            >
              Print Poster →
            </button>

            {/* Entries toggle — text-link style */}
            <button
              style={{ ...btn.base, background: 'transparent', color: '#555', fontSize: 12, fontFamily: 'var(--font-mono)', marginLeft: 'auto' }}
              onClick={showEntries ? () => setShowEntries(false) : loadEntries}
            >
              {showEntries ? 'Hide entries' : `View entries →`}
            </button>
          </div>

          {/* Race code editor */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#444', letterSpacing: '0.1em', textTransform: 'uppercase', flexShrink: 0 }}>
              Race Code
            </span>
            <input
              style={{
                flex: 1, minWidth: 120, maxWidth: 200,
                padding: '5px 10px',
                background: '#0d0d0d',
                border: `1px solid ${race.entry_code ? 'rgba(249,176,0,0.3)' : '#1e1e1e'}`,
                borderRadius: 4,
                color: race.entry_code ? '#f9b000' : '#555',
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
              value={codeInput}
              onChange={e => setCodeInput(e.target.value.toUpperCase())}
              placeholder="No code — predictions hidden"
            />
            <button
              style={{ ...btn.base, ...btn.ghost, fontSize: 12, padding: '5px 12px' }}
              onClick={() => saveCode(codeInput)}
              disabled={codeSaving}
            >
              {codeSaving ? '…' : 'Save'}
            </button>
            {race.entry_code && (
              <button
                style={{ ...btn.base, ...btn.danger, fontSize: 12, padding: '5px 12px' }}
                onClick={() => { setCodeInput(''); saveCode(''); }}
                disabled={codeSaving}
              >
                Clear
              </button>
            )}
            {codeStatus === 'saved' && (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#22c55e' }}>✓ Saved</span>
            )}
            {codeStatus === 'error' && (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#f9b000' }}>Failed</span>
            )}
          </div>

          {/* Video message */}
          <div style={{ marginTop: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#444', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Video Message
            </span>

            {race.video_url && !videoEditing ? (
              /* ── SET state: show what's active ── */
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
                  borderRadius: 4, padding: '4px 10px',
                  fontFamily: 'var(--font-mono)', fontSize: 11, color: '#93c5fd',
                }}>
                  ▶ {(() => {
                    const u = race.video_url;
                    if (/youtube\.com|youtu\.be/.test(u)) return 'YouTube video';
                    if (/vimeo\.com/.test(u)) return 'Vimeo video';
                    if (/\/s\/[a-zA-Z0-9]+/.test(u)) return 'Nextcloud file';
                    const fname = u.split('/').pop().split('?')[0];
                    return fname.length > 30 ? fname.slice(0, 27) + '…' : fname;
                  })()}
                </span>
                <button
                  style={{ ...btn.base, ...btn.ghost, fontSize: 11, padding: '3px 10px' }}
                  onClick={() => setVideoEditing(true)}
                >
                  Change
                </button>
                <button
                  style={{ ...btn.base, ...btn.danger, fontSize: 11, padding: '3px 10px' }}
                  onClick={() => { setVideoInput(''); saveVideo(''); setUploadFile(null); }}
                  disabled={videoSaving}
                >
                  {videoSaving ? '…' : 'Remove'}
                </button>
                <label style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontFamily: 'var(--font-mono)', fontSize: 11,
                  color: race.video_public ? '#22c55e' : '#555', cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={!!race.video_public} onChange={toggleVideoPublic} />
                  Public on race page
                </label>
              </div>
            ) : (
              /* ── EDITOR state: tabs for URL / Upload ── */
              <div style={{ marginTop: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  {['url', 'upload'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setVideoTab(tab)}
                      style={{
                        fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em',
                        textTransform: 'uppercase', padding: '3px 8px', borderRadius: 3, border: 'none',
                        cursor: 'pointer',
                        background: videoTab === tab ? 'rgba(59,130,246,0.2)' : 'transparent',
                        color: videoTab === tab ? '#93c5fd' : '#444',
                      }}
                    >
                      {tab === 'url' ? 'Paste URL' : 'Upload File'}
                    </button>
                  ))}
                  {videoEditing && (
                    <button
                      style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#444', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 4 }}
                      onClick={() => setVideoEditing(false)}
                    >
                      ✕ Cancel
                    </button>
                  )}
                </div>

                {videoTab === 'url' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      style={{
                        flex: 1, minWidth: 180, padding: '5px 10px',
                        background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 4,
                        color: '#aaa', fontFamily: 'var(--font-mono)', fontSize: 11,
                      }}
                      value={videoInput}
                      onChange={e => setVideoInput(e.target.value)}
                      placeholder="YouTube / Vimeo / Nextcloud / direct .mp4 URL"
                    />
                    <button
                      style={{ ...btn.base, ...btn.ghost, fontSize: 12, padding: '5px 12px' }}
                      onClick={() => saveVideo(videoInput)}
                      disabled={videoSaving}
                    >
                      {videoSaving ? '…' : 'Save'}
                    </button>
                    {videoStatus === 'saved' && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#22c55e' }}>✓ Saved</span>}
                    {videoStatus === 'error' && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#f9b000' }}>Failed</span>}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      type="file"
                      accept=".mp4,.mov,.webm,.mkv"
                      onChange={e => { setUploadFile(e.target.files[0] || null); setUploadError(null); }}
                      style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#555', flex: 1, minWidth: 180 }}
                    />
                    <button
                      style={{ ...btn.base, ...btn.ghost, fontSize: 12, padding: '5px 12px' }}
                      onClick={doUpload}
                      disabled={!uploadFile || uploadProgress !== null}
                    >
                      {uploadProgress !== null ? (uploadProgress < 100 ? `${uploadProgress}%` : '…') : 'Upload'}
                    </button>
                    {uploadProgress !== null && (
                      <>
                        <div style={{ flex: '1 1 100%', height: 3, background: '#1e1e1e', borderRadius: 2, marginTop: 4 }}>
                          <div style={{ height: '100%', width: `${uploadProgress}%`, background: '#93c5fd', borderRadius: 2, transition: 'width 0.2s' }} />
                        </div>
                        <span style={{ flex: '1 1 100%', fontFamily: 'var(--font-mono)', fontSize: 10, color: '#555', marginTop: 2 }}>
                          {uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Saving to cloud…'}
                        </span>
                      </>
                    )}
                    {uploadError && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#f9b000', flex: '1 1 100%' }}>{uploadError}</span>}
                    {videoStatus === 'saved' && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#22c55e' }}>✓ Uploaded</span>}
                  </div>
                )}
              </div>
            )}
          </div>


          {/* Results form */}
          {showResults && !race.official_top10 && (
            <ResultsForm
              race={race}
              onDone={() => { setShowResults(false); onRefresh(); }}
              onStatus={setStatusMsg}
            />
          )}

          {/* Manual live top 10 — shown when race is locked and no results yet */}
          {race.locked && !race.official_top10 && (
            <ManualLiveForm race={race} onRefresh={onRefresh} />
          )}

          {/* Entries table */}
          {showEntries && (
            <div style={{ marginTop: 14, animation: 'slideDown 0.2s ease' }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#444', marginBottom: 8 }}>
                {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
              </p>
              <div className="table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#444', fontWeight: 600, fontSize: 11, fontFamily: 'var(--font-mono)', borderBottom: '1px solid #1e1e1e' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#444', fontWeight: 600, fontSize: 11, fontFamily: 'var(--font-mono)', borderBottom: '1px solid #1e1e1e' }}>Predictions</th>
                      <th style={{ borderBottom: '1px solid #1e1e1e', width: 32 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(e => (
                      <tr key={e.id}>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #141414', color: '#c0c0c0', whiteSpace: 'nowrap' }}>
                          {e.user_name}
                        </td>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #141414' }}>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {e.predictions.map((acr, i) => (
                              <span key={i} style={{
                                fontFamily: 'var(--font-mono)', fontSize: 11,
                                padding: '2px 6px', borderRadius: 3,
                                background: '#141414', border: '1px solid #222', color: '#777',
                              }}>
                                P{i + 1} {acr}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid #141414', textAlign: 'right' }}>
                          <button
                            style={{ ...btn.base, ...btn.danger, padding: '3px 10px', fontSize: 11 }}
                            onClick={() => doDelete(e.id)}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                    {entries.length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ padding: '12px 8px', color: '#333', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                          No entries yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Complete state: archived details/video plus the existing results editor */}
      {!isCancelled && <PubSyncControls raceId={race.id} completed={isComplete} />}

      {isComplete && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
              {race.official_top10.map((acr, i) => (
                <span key={i} style={{
                  fontFamily: 'var(--font-mono)', fontSize: 11,
                  padding: '2px 6px', borderRadius: 3,
                  background: '#141414', border: '1px solid #1e1e1e', color: '#555',
                }}>
                  P{i + 1} {acr}
                </span>
              ))}
            </div>
            <button
              style={{ ...btn.base, background: 'transparent', color: '#444', fontSize: 11, fontFamily: 'var(--font-mono)', flexShrink: 0 }}
              onClick={() => setShowEditResults(r => !r)}
            >
              {showEditResults ? 'Cancel' : '✎ Edit Results'}
            </button>
          </div>
          <button type="button"
            aria-expanded={showDetails}
            aria-controls={`race-details-${race.id}`}
            style={{ ...btn.base, background: '#222', color: '#eee', marginTop: 12 }}
            onClick={() => setShowDetails(value => !value)}>
            {showDetails ? 'Hide race details' : 'View race details'}{race.video_url && !showDetails ? ' · Video available' : ''}
          </button>
          {showDetails && <AdminRaceDetails race={race} />}
          {showEditResults && (
            <ResultsForm
              race={race}
              onDone={() => { setShowEditResults(false); onRefresh(); }}
              onStatus={setStatusMsg}
            />
          )}
        </>
      )}

      {/* ── Persistent status bar ── */}
      {status && (
        <div style={{
          marginTop: 12,
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 10px', borderRadius: 4,
          background: status.type === 'success' ? 'rgba(34,197,94,0.08)' : 'rgba(249,176,0,0.08)',
          border: `1px solid ${status.type === 'success' ? 'rgba(34,197,94,0.2)' : 'rgba(249,176,0,0.2)'}`,
        }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: status.type === 'success' ? '#22c55e' : '#f9b000', flex: 1 }}>
            {status.type === 'success' ? '✓' : '✗'} {status.msg}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#333' }}>{status.time}</span>
          <button onClick={() => setStatus(null)} style={{ background: 'none', border: 'none', color: '#333', cursor: 'pointer', fontSize: 14, padding: '0 2px' }}>×</button>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function AdminPage() {
  const [adminKey, setAdminKeyState] = useState(() => {
    const stored = sessionStorage.getItem('f1tips_adminkey');
    if (stored) setAdminKey(stored);
    return stored || null;
  });
  const [races, setRaces]         = useState([]);
  const [syncYear, setSyncYear]   = useState(new Date().getFullYear());
  const [syncing, setSyncing]     = useState(false);
  const [syncMsg, setSyncMsg]     = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [openf1Status, setOpenf1Status] = useState(null); // { up, latencyMs }
  const [staffEmails, setStaffEmails]   = useState([]);
  const [staffInput, setStaffInput]     = useState('');
  const [staffSaving, setStaffSaving]   = useState(false);
  const statusInterval = useRef(null);

  const loadRaces = useCallback(() => {
    getAdminRaces().then(setRaces).catch(() => {});
  }, []);

  const loadStaff = useCallback(() => {
    getStaffEmails().then(setStaffEmails).catch(() => {});
  }, []);

  useEffect(() => {
    if (adminKey) loadRaces();
  }, [adminKey, loadRaces]);

  useEffect(() => {
    if (showSettings) loadStaff();
  }, [showSettings, loadStaff]);

  const handleAddStaff = async (e) => {
    e.preventDefault();
    const email = staffInput.trim().toLowerCase();
    if (!email) return;
    setStaffSaving(true);
    try {
      await addStaffEmail(email);
      setStaffInput('');
      loadStaff();
    } finally { setStaffSaving(false); }
  };

  const handleRemoveStaff = async (email) => {
    await removeStaffEmail(email);
    loadStaff();
  };

  useEffect(() => {
    if (!adminKey) return;
    const probe = () => getOpenF1Status().then(setOpenf1Status).catch(() => setOpenf1Status({ up: false }));
    probe();
    statusInterval.current = setInterval(probe, 30_000);
    return () => clearInterval(statusInterval.current);
  }, [adminKey]);

  const handleSync = async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const r = await syncSeason(syncYear);
      setSyncMsg({ type: 'success', text: `Synced ${r.synced} races for ${syncYear}` });
      loadRaces();
    } catch (e) {
      setSyncMsg({ type: 'error', text: e.response?.data?.error || e.message });
    } finally { setSyncing(false); }
  };

  const handleExport = async () => {
    const blob = await exportEntries();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `f1tips-${new Date().getFullYear()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!adminKey) return <AdminKeyGate onKey={setAdminKeyState} />;

  const now       = Date.now();
  const nextRaceId = races.find(r => !r.locked && !r.cancelled && r.race_date && new Date(r.race_date) > now)?.id;

  // Sort: incomplete/active first, cancelled + complete last
  const sorted = [...races].sort((a, b) => {
    const order = { overdue: 0, locked: 1, open: 2, upcoming: 3, complete: 4, cancelled: 5 };
    return (order[getRaceState(a)] ?? 2) - (order[getRaceState(b)] ?? 2);
  });

  return (
    <div>
      <style>{kf}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: showSettings ? 16 : 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showSettings ? 16 : 0 }}>
          <div>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#e8002d', letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 4 }}>
              Bryggeriet Ångkvarn
            </p>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1 }}>
              Admin Panel
            </h1>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {openf1Status && (
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em',
                padding: '4px 10px', borderRadius: 20,
                background: openf1Status.up ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${openf1Status.up ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                color: openf1Status.up ? '#22c55e' : '#ef4444',
              }}>
                <span style={{ marginRight: 5 }}>●</span>
                OPENF1 {openf1Status.up ? `${openf1Status.latencyMs}ms` : 'DOWN'}
              </span>
            )}
            <button
              onClick={() => setShowSettings(s => !s)}
              style={{
                ...btn.base, background: 'transparent',
                color: showSettings ? '#f9b000' : '#444',
                border: `1px solid ${showSettings ? 'rgba(249,176,0,0.3)' : '#222'}`,
                fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.08em',
              }}
            >
              ⚙ Settings
            </button>
            {[
              ['/pitwall', 'Pit Wall'],
              ['/pitwall2', 'Pit Wall 2'],
              ['/pitwall3', 'Pit Wall 3'],
            ].map(([to, label]) => <Link key={to} to={to} target="_blank" rel="noopener noreferrer" style={{
              fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.1em',
              color: '#f9b000', textDecoration: 'none',
              border: '1px solid rgba(249,176,0,0.3)', borderRadius: 4,
              padding: '8px 16px',
            }}>
              {label} →
            </Link>)}
          </div>
        </div>

        {/* Settings panel — expands below header */}
        {showSettings && (
          <>
          <Link to="/admin/raffles" style={{ display: 'inline-block', color: '#f9b000', marginBottom: 15, padding: '10px 16px', border: '1px solid #f9b000', borderRadius: 4, textDecoration: 'none', fontFamily: 'var(--font-display)' }}>
            Event raffles →
          </Link>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, animation: 'slideDown 0.2s ease', marginBottom: 28 }}>
            <div style={{ background: '#111', borderRadius: 6, padding: 20, border: '1px solid #1a1a1a' }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#555', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>
                Season Sync
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <input
                  style={{ padding: '7px 10px', background: '#0d0d0d', border: '1px solid #2a2a2a', borderRadius: 4, color: '#f0f0f0', fontSize: 13, width: 90, fontFamily: 'var(--font-mono)' }}
                  type="number"
                  value={syncYear}
                  onChange={e => setSyncYear(Number(e.target.value))}
                />
                <button style={{ ...btn.base, ...btn.yellow, flex: 1 }} onClick={handleSync} disabled={syncing}>
                  {syncing ? 'Syncing…' : 'Sync from OpenF1'}
                </button>
              </div>
              {syncMsg && (
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: syncMsg.type === 'success' ? '#22c55e' : '#f9b000' }}>
                  {syncMsg.text}
                </p>
              )}
            </div>
            <div style={{ background: '#111', borderRadius: 6, padding: 20, border: '1px solid #1a1a1a' }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#555', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>
                Export Data
              </p>
              <button style={{ ...btn.base, ...btn.ghost, width: '100%' }} onClick={handleExport}>
                Download CSV (name, email, points)
              </button>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#333', marginTop: 8 }}>
                All participants, total season points and races scored.
              </p>
              <AdminRaceLogDownload races={races} buttonStyle={{ ...btn.base, ...btn.ghost, color: '#ccc' }} />
            </div>
            <div style={{ background: '#111', borderRadius: 6, padding: 20, border: '1px solid #1a1a1a' }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#555', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>
                Staff / Internal Entries
              </p>
              <form onSubmit={handleAddStaff} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <input
                  style={{ flex: 1, minWidth: 0, padding: '7px 10px', background: '#0d0d0d', border: '1px solid #2a2a2a', borderRadius: 4, color: '#f0f0f0', fontSize: 13, fontFamily: 'var(--font-mono)' }}
                  type="email"
                  placeholder="employee@email.com"
                  value={staffInput}
                  onChange={e => setStaffInput(e.target.value)}
                />
                <button type="submit" style={{ ...btn.base, ...btn.yellow }} disabled={staffSaving || !staffInput.trim()}>
                  Add
                </button>
              </form>
              {staffEmails.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 120, overflowY: 'auto' }}>
                  {staffEmails.map(s => (
                    <div key={s.email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 8px', background: '#0d0d0d', borderRadius: 4 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.email}
                      </span>
                      <button
                        onClick={() => handleRemoveStaff(s.email)}
                        style={{ ...btn.base, ...btn.danger, padding: '2px 8px', fontSize: 11, flexShrink: 0 }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#333' }}>
                  No staff flagged — everyone shows in standings.
                </p>
              )}
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#333', marginTop: 8 }}>
                Flagged emails are hidden from all public standings/leaderboards, but keep predicting and scoring normally.
              </p>
            </div>
          </div>
          </>
        )}
      </div>

      {/* ── Races ── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#555', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            {races.length} Races · {races.filter(r => r.official_top10).length} Complete
          </p>
        </div>
        {races.length === 0 && (
          <p style={{ color: '#444', fontSize: 14, fontFamily: 'var(--font-mono)' }}>No races found — run a sync below.</p>
        )}
        {sorted.map(race => (
          <RaceAdmin
            key={race.id}
            race={race}
            onRefresh={loadRaces}
            isNext={race.id === nextRaceId}
          />
        ))}
      </div>

    </div>
  );
}
